import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, DatePicker, Empty, Input, Tooltip, notification } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { AiOutlineStop } from 'react-icons/ai';
import { LuSearch } from 'react-icons/lu';
import dayjs from 'dayjs';
import { debounce } from 'lodash';
import { saveAs } from 'file-saver';
import { useTheme } from 'styled-components';

/* Components */
import { PageHeader } from 'components/PageHeader';
import { PageWrapper } from 'components/PageWrapper';
import { Heading1 } from 'components/Heading1';
import { Table } from 'components/Table';
import { TextM, TextS } from 'components/Text';
import { BreadcrumbContainer } from 'components/Breadcrumb';
import { DownloadIcon } from 'components/Icons/Download';

/* Types, services and helpers */
import {
	PaymentReceipt,
	PaymentReceiptDownloadState,
	PaymentReceiptFileStatus,
	PaymentReceiptOrder,
	PaymentReceiptSort,
} from './types';
import { PaymentReceiptsService } from 'modules/escrow/services';
import { EscrowIcon } from 'modules/escrow/constants/routes';
import { useBreadcrumb } from 'modules/core/context/AppContext';
import { PostingTypeDescription } from 'constants/posting';
import { PaginationInfo } from 'types/List';
import { ApiError } from 'types/ApiError';
import {
	formatCurrency,
	normalizeBankAccount,
	normalizeCpfCnpj,
} from 'helpers/normalizers';

/* Styles */
import { EllipsisText, FiltersContainer } from './styles';

const INITIAL_PAGINATION: PaginationInfo = {
	currentPage: 1,
	pageSize: 10,
};

type SortingState = {
	sort?: PaymentReceiptSort;
	order?: PaymentReceiptOrder;
};

const PaymentReceipts = () => {
	const theme = useTheme();
	const { setBreadcrumb } = useBreadcrumb();
	const [api, contextHolder] = notification.useNotification();

	const [paginationInfo, setPaginationInfo] =
		useState<PaginationInfo>(INITIAL_PAGINATION);
	const [beneficiaryInput, setBeneficiaryInput] = useState('');
	const [beneficiary, setBeneficiary] = useState('');
	const [dateRange, setDateRange] = useState<
		[dayjs.Dayjs | null, dayjs.Dayjs | null] | null
	>(null);
	// Sem sort a API já devolve do mais recente para o mais antigo,
	// por isso o estado inicial é vazio (e não sort=date).
	const [sorting, setSorting] = useState<SortingState>({});
	const [downloadingId, setDownloadingId] = useState<string | null>(null);
	const [downloadStates, setDownloadStates] = useState<
		Record<string, PaymentReceiptDownloadState>
	>({});

	const dateFrom = dateRange?.[0]?.format('YYYY-MM-DD');
	const dateTo = dateRange?.[1]?.format('YYYY-MM-DD');

	const debouncedSetBeneficiary = useMemo(
		() =>
			debounce((value: string) => {
				setBeneficiary(value);
				setPaginationInfo(INITIAL_PAGINATION);
			}, 400),
		[],
	);

	useEffect(
		() => () => debouncedSetBeneficiary.cancel(),
		[debouncedSetBeneficiary],
	);

	useEffect(() => {
		setBreadcrumb([
			{
				href: '/escrow/accounts',
				title: (
					<BreadcrumbContainer>
						<EscrowIcon />
						<span>Conta Escrow</span>
					</BreadcrumbContainer>
				),
			},
			{
				title: 'Comprovantes de pagamento',
			},
		]);
	}, [setBreadcrumb]);

	const { data, isLoading } = useQuery({
		queryKey: [
			'paymentReceipts',
			paginationInfo,
			beneficiary,
			dateFrom,
			dateTo,
			sorting,
		],
		queryFn: () =>
			PaymentReceiptsService.getPaymentReceipts({
				page: paginationInfo.currentPage,
				size: paginationInfo.pageSize,
				...(beneficiary ? { beneficiary } : {}),
				...(dateFrom ? { dateFrom } : {}),
				...(dateTo ? { dateTo } : {}),
				// order só vale acompanhado de sort
				...(sorting.sort
					? { sort: sorting.sort, order: sorting.order }
					: {}),
			}),
		placeholderData: keepPreviousData,
	});

	const hasActiveFilters = Boolean(
		beneficiaryInput || beneficiary || dateRange || sorting.sort,
	);

	const handleClearFilters = useCallback(() => {
		debouncedSetBeneficiary.cancel();
		setBeneficiaryInput('');
		setBeneficiary('');
		setDateRange(null);
		setSorting({});
		setPaginationInfo(INITIAL_PAGINATION);
	}, [debouncedSetBeneficiary]);

	const handleDownload = useCallback(
		async (receipt: PaymentReceipt) => {
			setDownloadingId(receipt.posting_id);
			try {
				const file = await PaymentReceiptsService.getPaymentReceiptFile(
					receipt.posting_id,
				);

				// 202: o PDF foi reenfileirado e ainda não existe (url vem nula).
				// Não é erro — vale tentar de novo em alguns segundos.
				if (
					file.status !== PaymentReceiptFileStatus.READY ||
					!file.url
				) {
					setDownloadStates(states => ({
						...states,
						[receipt.posting_id]: 'pending',
					}));
					api.info({
						message: 'Gerando seu comprovante',
						description:
							'O arquivo ainda está sendo gerado. Tente novamente em instantes.',
					});
					return;
				}

				setDownloadStates(states => {
					const { [receipt.posting_id]: _removed, ...rest } = states;
					return rest;
				});
				saveAs(file.url, file.file_name ?? undefined);
			} catch (error) {
				const response = error as ApiError;

				// 404: não existe, não é SUCCESS ou é de outra instituição — repetir não resolve.
				if (response?.status === 404) {
					setDownloadStates(states => ({
						...states,
						[receipt.posting_id]: 'unavailable',
					}));
					api.error({
						message: 'Comprovante indisponível',
						description:
							'Não encontramos o comprovante desta movimentação.',
					});
					return;
				}

				api.error({
					message: 'Não foi possível baixar o comprovante',
					description:
						response?.data?.message ??
						'Tente novamente em instantes.',
				});
			} finally {
				setDownloadingId(null);
			}
		},
		[api],
	);

	const columns: ColumnsType<PaymentReceipt> = [
		{
			title: 'Data do pagamento',
			key: 'date',
			sorter: true,
			width: '14%',
			render: (receipt: PaymentReceipt) => (
				<div>
					<TextM weight="bold">
						{dayjs(receipt.executed_at).format('DD/MM/YYYY')}
					</TextM>
					<TextS weight="normal" color={theme.textSecondary}>
						às {dayjs(receipt.executed_at).format('HH:mm')}
					</TextS>
				</div>
			),
		},
		{
			title: 'Titular',
			key: 'name',
			sorter: true,
			width: '20%',
			render: (receipt: PaymentReceipt) => (
				<div>
					<TextM weight="bold">{receipt.holder_name}</TextM>
					<TextS weight="normal" color={theme.textSecondary}>
						{normalizeCpfCnpj(receipt.holder_taxpayer_id)}
					</TextS>
				</div>
			),
		},
		{
			title: 'Conta',
			width: '14%',
			render: (receipt: PaymentReceipt) => (
				<div>
					<TextM weight="normal">
						{receipt.account?.account
							? normalizeBankAccount(receipt.account.account)
							: '-'}
					</TextM>
					<TextS weight="normal" color={theme.textSecondary}>
						Ag. {receipt.account?.branch ?? '-'}
					</TextS>
				</div>
			),
		},
		{
			title: 'Beneficiário',
			width: '18%',
			render: (receipt: PaymentReceipt) =>
				receipt.beneficiary_name ? (
					<Tooltip title={receipt.beneficiary_name}>
						<EllipsisText>
							<TextM weight="normal">
								{receipt.beneficiary_name}
							</TextM>
						</EllipsisText>
					</Tooltip>
				) : (
					<TextM weight="normal">-</TextM>
				),
		},
		{
			title: 'Tipo',
			width: '8%',
			render: (receipt: PaymentReceipt) => (
				<TextM weight="normal" color={theme.textSecondary}>
					{PostingTypeDescription[receipt.type] ?? receipt.type}
				</TextM>
			),
		},
		{
			title: 'Descrição',
			width: '16%',
			render: (receipt: PaymentReceipt) =>
				receipt.description ? (
					<Tooltip title={receipt.description}>
						<EllipsisText>
							<TextM weight="normal" color={theme.textSecondary}>
								{receipt.description}
							</TextM>
						</EllipsisText>
					</Tooltip>
				) : (
					<TextM weight="normal" color={theme.textSecondary}>
						-
					</TextM>
				),
		},
		{
			title: 'Valor',
			key: 'amount',
			sorter: true,
			width: '12%',
			render: (receipt: PaymentReceipt) => (
				<TextM weight="bold">{formatCurrency(receipt.amount)}</TextM>
			),
		},
		{
			title: 'Comprovante',
			width: '14%',
			render: (receipt: PaymentReceipt) => {
				const state = downloadStates[receipt.posting_id];
				const isUnavailable = state === 'unavailable';
				const isPending = state === 'pending';

				return (
					<Tooltip
						title={
							isUnavailable
								? 'Comprovante indisponível'
								: isPending
									? 'Comprovante ainda em geração'
									: 'Baixar comprovante'
						}
					>
						<Button
							type="link"
							style={{ padding: 0 }}
							disabled={isUnavailable}
							loading={downloadingId === receipt.posting_id}
							icon={
								<DownloadIcon
									size="16"
									color={
										isUnavailable
											? theme.textSecondary
											: theme.primary
									}
								/>
							}
							onClick={() => handleDownload(receipt)}
						>
							{isPending ? 'Tentar novamente' : 'Baixar'}
						</Button>
					</Tooltip>
				);
			},
		},
	];

	return (
		<>
			{contextHolder}
			<PageHeader>
				<Heading1>Comprovantes de pagamento</Heading1>
			</PageHeader>
			<PageWrapper>
				<FiltersContainer>
					<Input
						size="large"
						allowClear
						style={{ maxWidth: 320 }}
						placeholder="Buscar pelo nome do beneficiário"
						prefix={<LuSearch size={16} />}
						value={beneficiaryInput}
						onChange={event => {
							setBeneficiaryInput(event.target.value);
							debouncedSetBeneficiary(event.target.value);
						}}
					/>
					<DatePicker.RangePicker
						size="large"
						format="DD/MM/YYYY"
						value={dateRange}
						maxDate={dayjs()}
						onChange={values => {
							setDateRange(values);
							setPaginationInfo(INITIAL_PAGINATION);
						}}
					/>
					<Button
						size="large"
						icon={<AiOutlineStop size={16} />}
						disabled={!hasActiveFilters}
						onClick={handleClearFilters}
					>
						Limpar filtros
					</Button>
				</FiltersContainer>
				<Table
					scroll={{ x: 1200 }}
					columns={columns}
					rowKey={(record: PaymentReceipt) => record.posting_id}
					dataSource={data?.content || []}
					loading={isLoading}
					locale={{
						emptyText: (
							<Empty description="Nenhum comprovante encontrado" />
						),
					}}
					onChange={(_, __, sorter) => {
						const column = Array.isArray(sorter)
							? sorter[0]
							: sorter;

						if (!column?.order || !column?.columnKey) {
							setSorting({});
							return;
						}

						setSorting({
							sort: column.columnKey as PaymentReceiptSort,
							order: column.order === 'ascend' ? 'asc' : 'desc',
						});
						setPaginationInfo(pagination => ({
							...pagination,
							currentPage: 1,
						}));
					}}
					pagination={{
						total: data?.total_elements || 0,
						showTotal(total) {
							const currentSize =
								paginationInfo.currentPage *
								paginationInfo.pageSize;
							return `${
								currentSize > total ? total : currentSize
							} de ${total}`;
						},
						pageSizeOptions: ['10', '30', '60', '90'],
						showSizeChanger: true,
						pageSize: paginationInfo.pageSize,
						current: paginationInfo.currentPage,
						onChange(page, pageSize) {
							setPaginationInfo({
								currentPage: page,
								pageSize,
							});
						},
					}}
				/>
			</PageWrapper>
		</>
	);
};

export default PaymentReceipts;
